<template>
  <app-page
    title="Which award do you want to duplicate?"
    subtitle="Pick an award to use as the starting point. All of its settings are copied into the new awards — you can change anything before they are created. All awards are shown; disabled awards are greyed out."
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
  >
    <template #extraTitle>
      <active-company-pill class="ml-2" />
    </template>

    <template #custom-content>
      <b-col>
        <div v-if="loading" class="text-center py-5">
          <b-spinner />
        </div>
        <div v-else-if="programs.length === 0" class="text-muted py-5 text-center">
          You have no awards to duplicate yet.
        </div>
        <template v-else>
          <div class="duplicate-source__table-card">
            <awards-table selectable :programs="programs" :selected-id="selectedId" @select="selectProgram" />
          </div>
        </template>
      </b-col>
    </template>

    <template #footer>
      <div class="duplicate-source__footer">
        <b-button variant="outline-secondary" @click="onCancel">Cancel</b-button>
        <div class="duplicate-source__footer-actions">
          <div class="duplicate-source__copies">
            <span class="duplicate-source__copies-label">Number of copies</span>
            <b-form-input
              type="number"
              min="1"
              max="20"
              :value="copies"
              class="duplicate-source__copies-input"
              @change="setCopies($event)"
            />
          </div>
          <b-button variant="primary" :disabled="!selectedId || !copies" @click="onNext">{{ nextLabel }}</b-button>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import AppPage from '@/components/AppPage.vue';
import AwardsTable from '@/pages/recognition/components/AwardsTable.vue';

const $router = useRouter();

const loading = ref(true);
const programs = ref([]);
const selectedId = ref(null);
const copies = ref(1);

// Clamps a typed value into 1..20; blank/non-numeric leaves it empty so Next stays disabled.
function setCopies(value) {
  if (value === '' || value === null || value === undefined) {
    copies.value = null;
    return;
  }
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) {
    copies.value = null;
    return;
  }
  copies.value = Math.min(20, Math.max(1, n));
}

const nextLabel = computed(() => {
  if (!copies.value) {
    return 'Create awards';
  }
  return `Create ${copies.value} ${copies.value === 1 ? 'award' : 'awards'}`;
});

function onCancel() {
  $router.push({ name: 'recognition' });
}

onMounted(async () => {
  try {
    programs.value = await ApiClient.getRecognitionPrograms({ includeInactive: true });
  } catch (e) {
    programs.value = [];
  } finally {
    loading.value = false;
  }
});

function selectProgram(program) {
  selectedId.value = program.recognition_award_program_id;
}

function onNext() {
  if (!selectedId.value || !copies.value) {
    return;
  }
  $router.push({
    name: 'recognition-group-editor',
    query: { sourceId: selectedId.value, copies: String(copies.value) },
  });
}
</script>

<style scoped lang="scss">
.duplicate-source {
  &__copies-input {
    width: 3rem;
    text-align: center;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    // Hide the browser's native number-input spinner arrows.
    appearance: textfield;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__table-card {
    background: var(--palette-color-base-white);
    border: 1px solid var(--palette-color-default-lighten-90);
    border-radius: 0.5rem;
    overflow: hidden;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem 1.5rem;
  }

  &__footer-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__copies {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__copies-label {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-20);
  }
}
</style>
